import type { ReactNode } from "react";
import { EmptyState } from "./EmptyState.tsx";

export interface DataColumn<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
}

interface DataTableProps<T> {
  columns: DataColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  emptyTitle?: string;
  emptyDescription?: string;
  caption: string;
}

function display(value: unknown): string {
  return value === null || value === undefined || value === "" ? "—" : String(value);
}

/**
 * Tabela somente leitura com cabeçalhos explícitos, rolagem horizontal em telas pequenas e
 * estado vazio padronizado.
 */
export function DataTable<T extends Record<string, unknown>>({
  columns,
  rows,
  rowKey,
  emptyTitle = "Nenhum registro encontrado",
  emptyDescription = "Quando houver dados operacionais, eles aparecerão aqui.",
  caption,
}: DataTableProps<T>) {
  if (rows.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <table className="w-full text-left text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-muted/60">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className="whitespace-nowrap px-3 py-2 font-medium text-muted-foreground"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={rowKey(row, index)} className="border-t">
              {columns.map((column) => (
                <td key={column.key} className="max-w-56 truncate px-3 py-2">
                  {column.render ? column.render(row) : display(row[column.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
